import { useTranslation } from 'react-i18next';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { BdStatusBadge } from './BdStatusBadge.js';
import type { BdSectionStatus } from './bd-defs.js';
import type { BdArtifactDef } from '../../../lib/api/basic-design.js';

export interface BdSectionProps {
  /** 節の定義（節番号・名前・何を書くか・必須かどうか） */
  def: BdArtifactDef;
  /** 節の状態 */
  status: BdSectionStatus;
  /** 保存済みの行数。まだ無い節では null */
  count: number | null;
  /** 「該当なし」で保存したときの理由。理由を求める前に保存した節では空 */
  notApplicableReason?: string;
  /** この節を編集中か。編集中は見出しの右のボタンと空の案内を出さない */
  editing: boolean;
  /** 直前の保存で残った注意。編集中は出さない */
  warnings: string[];
  /** 「該当なし」で保存している最中か */
  markingNotApplicable: boolean;
  /** 編集を開く */
  onEdit: () => void;
  /** 任意の節を 0 行（該当なし）で保存する。必須の節では渡さない */
  onNotApplicable?: () => void;
  children: React.ReactNode;
}

/**
 * 基本設計の節。見出し（節番号・名前・必須・状態）・編集ボタン・保存後の注意・空の案内を持つ。
 *
 * まだ書いていない節は見出しの右にボタンを置かず、本文の空の案内に「書く」を大きく出す
 * （何も無い画面で小さなボタンを探させない）。任意の節は空の案内に「該当なし」を並べる。
 * 「該当なし」は「まだ書いていない」と違い、書く人が当てはまらないと決めたことを残すもの。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 基本設計の節を、見出し・状態・編集ボタン・該当なしボタン・保存後の注意・空の案内つきで出す
 * @graph-connects BdStatusBadge [calls] 節の状態の札
 */
export function BdSection({
  def,
  status,
  count,
  notApplicableReason = '',
  editing,
  warnings,
  markingNotApplicable,
  onEdit,
  onNotApplicable,
  children,
}: BdSectionProps) {
  const { t } = useTranslation();
  const empty = status === 'notSaved' && count === null;
  const notApplicable = status === 'notApplicable';

  return (
    <section className="space-y-3" aria-label={def.title}>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs tabular-nums text-[var(--color-text-muted)]">
              {def.section}
            </span>
            <h2 className="text-base font-semibold text-[var(--color-text-primary)]">
              {def.title}
            </h2>
            <Badge size="sm" variant={def.requirement === 'required' ? 'info' : 'default'}>
              {def.requirement === 'required' ? t('bd.required') : t('bd.optional')}
            </Badge>
            <BdStatusBadge status={status} count={count} />
          </div>
          <p className="mt-0.5 text-xs text-[var(--color-text-muted)]">{def.summary}</p>
        </div>
        {editing || empty ? null : (
          <Button
            variant="secondary"
            size="sm"
            aria-label={t('bd.section.editAria', { title: def.title })}
            onClick={onEdit}
          >
            {t('bd.section.edit')}
          </Button>
        )}
      </div>

      {!editing && warnings.length > 0 ? (
        <div className="rounded-lg border border-[var(--color-warning)]/30 bg-[var(--color-warning-muted)] px-4 py-3">
          <h3 className="text-xs font-semibold text-[var(--color-warning-text)]">
            {t('bd.section.savedWithWarnings')}
          </h3>
          <ul className="mt-1 list-disc pl-5 text-sm text-[var(--color-text-primary)]">
            {warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {editing ? (
        children
      ) : empty ? (
        <div className="rounded-xl border border-dashed border-[var(--color-border-hover)] px-6 py-8 text-center">
          <p className="text-sm text-[var(--color-text-secondary)]">{t('bd.section.notSaved')}</p>
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            <Button size="sm" onClick={onEdit}>
              {t('bd.section.write', { title: def.title })}
            </Button>
            {onNotApplicable === undefined ? null : (
              <Button
                size="sm"
                variant="secondary"
                loading={markingNotApplicable}
                disabled={markingNotApplicable}
                onClick={onNotApplicable}
              >
                {t('bd.section.markNotApplicable')}
              </Button>
            )}
          </div>
        </div>
      ) : notApplicable || count === 0 ? (
        <div className="rounded-xl border border-dashed border-[var(--color-border)] px-6 py-6 text-center text-sm text-[var(--color-text-secondary)]">
          <p>{notApplicable ? t('bd.section.notApplicableText') : t('bd.section.noRows')}</p>
          {notApplicable ? (
            <p className="mt-1 text-[var(--color-text-primary)]">
              {notApplicableReason === ''
                ? t('bd.section.notApplicableNoReason')
                : t('bd.section.notApplicableReason', { reason: notApplicableReason })}
            </p>
          ) : null}
        </div>
      ) : (
        children
      )}
    </section>
  );
}
